import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { FileDropZone, getDocument } from '@/domains/document'
import { captureException } from '@/domains/observability'
import { AddressInput } from '@/lib/address-input'

import { getCompanyGeneralInfo, updateCompanyGeneralInfo } from './api'
import { FormSkeleton } from './form-skeleton'
import { GeneralInfoFormProvider } from './general-info-provider'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'
import { useGeneralInfoForm } from './use-general-info-form'

const DESCRIPTION_MAX = 500
const LOGO_MAX_BYTES = 2 * 1024 * 1024
/* must stay a subset of DOCUMENT_CONTENT_TYPES on the api, or the local check
 * passes and the upload dies on a raw schema error. svg is rejected there on
 * purpose (it executes script in an img tag), so it cannot be offered here. */
const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp'

/**
 * company-level: reads + writes the shared company identity, so both the
 * supplier and buyer flows use it.
 */
export function CompanyGeneralInfoForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const { data: generalInfo, isError } = useQuery({
    queryKey: ['company-general-info'],
    queryFn: ({ signal }) => getCompanyGeneralInfo(signal)
  })

  if (isError) {
    return (
      <div className='w-full rounded-2xl border border-red-200 bg-red-50 p-6 text-[13px] text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300'>
        Couldn't load your company information. Refresh the page or try again
        later.
      </div>
    )
  }
  if (!generalInfo) return <FormSkeleton />

  return (
    <GeneralInfoFormProvider generalInfo={generalInfo} onSuccess={onSuccess}>
      <FormBody submitLabel={submitLabel} />
    </GeneralInfoFormProvider>
  )
}

function FormBody({ submitLabel }: { submitLabel: string }) {
  const { state, derived, dispatch, isSubmitting, handleSubmit } =
    useGeneralInfoForm()

  return (
    <form onSubmit={handleSubmit} className='w-full'>
      <div className='rounded-2xl border border-border bg-page'>
        <div className='flex flex-col gap-5 p-6'>
          <TextField
            id='companyName'
            label='Company name'
            required
            value={state.companyName}
            onChange={v =>
              dispatch({ type: 'setString', field: 'companyName', value: v })
            }
          />

          <LogoField
            logoId={state.logoId}
            companyName={state.companyName}
            onChange={value => dispatch({ type: 'setLogoId', value })}
          />

          <div className='flex flex-col gap-1.5'>
            <div className='flex items-baseline justify-between'>
              <Label htmlFor='description'>Description</Label>
              <span className='text-[11px] text-ink-muted'>
                {state.description.length}/{DESCRIPTION_MAX}
              </span>
            </div>
            <Textarea
              id='description'
              value={state.description}
              maxLength={DESCRIPTION_MAX}
              rows={3}
              placeholder='Who you are and what do you do.'
              onChange={event =>
                dispatch({
                  type: 'setString',
                  field: 'description',
                  value: event.target.value
                })
              }
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='website'>
              Website<span className='text-red-500'>*</span>
            </Label>
            <Input
              id='website'
              type='text'
              value={state.website}
              onChange={e =>
                dispatch({
                  type: 'setString',
                  field: 'website',
                  value: e.target.value
                })
              }
              onBlur={() => dispatch({ type: 'touch', field: 'website' })}
              placeholder='https://'
              aria-invalid={
                (state.websiteTouched && derived.websiteInvalid) || undefined
              }
            />
            {state.websiteTouched && derived.websiteInvalid && (
              <p className='text-[12px] text-red-600'>
                Enter a website like <code>example.com</code> or{' '}
                <code>https://example.com</code>.
              </p>
            )}
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='location'>
              Headquarter address<span className='text-red-500'>*</span>
            </Label>
            <AddressInput
              id='location'
              value={state.location}
              onChange={v =>
                dispatch({ type: 'setString', field: 'location', value: v })
              }
              onInvalidate={() => dispatch({ type: 'addressInvalidated' })}
              onSelect={details =>
                dispatch({
                  type: 'addressPicked',
                  countryCode: details.countryCode,
                  country: details.country ?? '',
                  region: details.region ?? '',
                  latitude: details.latitude,
                  longitude: details.longitude
                })
              }
              placeholder='e.g. 1725 Slocum Rd, Scranton, PA 18505, USA'
              error={
                derived.locationUnresolved
                  ? 'Select your address from the suggestions so we can record your country.'
                  : undefined
              }
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='yearFounded'>
              Year founded<span className='text-red-500'>*</span>
            </Label>
            <Input
              id='yearFounded'
              type='text'
              inputMode='numeric'
              placeholder='1949'
              value={state.yearFounded}
              onChange={e =>
                dispatch({
                  type: 'setString',
                  field: 'yearFounded',
                  value: e.target.value.replace(/\D/g, '').slice(0, 4)
                })
              }
              onBlur={() => dispatch({ type: 'touch', field: 'yearFounded' })}
              aria-invalid={
                (state.yearFoundedTouched && derived.yearFoundedInvalid) ||
                undefined
              }
            />
            {state.yearFoundedTouched && derived.yearFoundedInvalid && (
              <p className='text-[12px] text-red-600'>
                Enter a 4-digit year between 1700 and {derived.currentYear}.
              </p>
            )}
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='dunsNumber'>DUNS number</Label>
            <Input
              id='dunsNumber'
              type='text'
              inputMode='numeric'
              placeholder='123456789'
              value={state.dunsNumber}
              onChange={e =>
                dispatch({
                  type: 'setString',
                  field: 'dunsNumber',
                  value: e.target.value.replace(/\D/g, '').slice(0, 9)
                })
              }
              onBlur={() => dispatch({ type: 'touch', field: 'dunsNumber' })}
              aria-invalid={
                (state.dunsNumberTouched && derived.dunsNumberInvalid) ||
                undefined
              }
            />
            {state.dunsNumberTouched && derived.dunsNumberInvalid && (
              <p className='text-[12px] text-red-600'>DUNS must be 9 digits.</p>
            )}
          </div>

          <Checkbox
            id='isPublic'
            label='Publicly traded company'
            checked={state.isPublic}
            onChange={v =>
              dispatch({ type: 'setBool', field: 'isPublic', value: v })
            }
          />

          <Checkbox
            id='isSubsidiary'
            label='Subsidiary of another company'
            checked={state.isSubsidiary}
            onChange={v =>
              dispatch({ type: 'setBool', field: 'isSubsidiary', value: v })
            }
          />

          {state.isSubsidiary && (
            <TextField
              id='parentCompanyName'
              label='Parent company name'
              required
              value={state.parentCompanyName}
              onChange={v =>
                dispatch({
                  type: 'setString',
                  field: 'parentCompanyName',
                  value: v
                })
              }
            />
          )}
        </div>

        {state.submitError && (
          <p className='border-t border-border px-6 py-3 text-sm text-red-600'>
            {state.submitError}
          </p>
        )}

        <StepFooter variant='divider'>
          <PrimaryButton
            type='submit'
            disabled={!derived.isValid || isSubmitting}
          >
            {isSubmitting ? 'Saving…' : submitLabel}
          </PrimaryButton>
        </StepFooter>
      </div>
    </form>
  )
}

function TextField({
  id,
  label,
  required,
  value,
  onChange
}: {
  id: string
  label: string
  required?: boolean
  value: string
  onChange: (next: string) => void
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <Label htmlFor={id}>
        {label}
        {required && <span className='text-red-500'>*</span>}
      </Label>
      <Input id={id} value={value} onChange={e => onChange(e.target.value)} />
    </div>
  )
}

function Checkbox({
  id,
  label,
  checked,
  onChange
}: {
  id: string
  label: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <label htmlFor={id} className='flex cursor-pointer items-center gap-2'>
      <input
        id={id}
        type='checkbox'
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className='size-4 cursor-pointer rounded border-border accent-accent'
      />
      <span className='text-[13px] text-ink'>{label}</span>
    </label>
  )
}

function LogoField({
  logoId,
  companyName,
  onChange
}: {
  logoId: string | null
  companyName: string
  onChange: (next: string | null) => void
}) {
  const initials = companyName
    .split(/\s+/)
    .map(part => part[0]?.toUpperCase() ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')

  const queryClient = useQueryClient()
  const { data: existing } = useQuery({
    queryKey: ['document', logoId],
    queryFn: ({ signal }) => getDocument(logoId!, signal),
    enabled: Boolean(logoId),
    staleTime: 60_000
  })

  /**
   * the logo persists on pick/remove rather than waiting for the form save,
   * so the sidebar avatar updates right away and the backend retires the
   * previous logo (no orphaned uploads). invalidating setup refreshes the
   * sidebar's companyLogoId.
   */
  const persist = useMutation({
    mutationFn: (next: string | null) =>
      updateCompanyGeneralInfo({ logoId: next }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['setup'] }),
        queryClient.invalidateQueries({ queryKey: ['company-general-info'] })
      ]),
    onError: error => captureException(error)
  })

  const value =
    logoId && existing
      ? { documentId: logoId, fileName: existing.document.fileName }
      : null

  return (
    <div className='flex flex-col gap-1.5'>
      <Label>Logo</Label>
      <div className='flex flex-col items-start gap-3 sm:flex-row sm:items-center'>
        {value && existing ? (
          <img
            src={existing.downloadUrl}
            alt='Current logo'
            className='size-16 shrink-0 rounded-full border border-border object-cover'
          />
        ) : (
          <div className='grid size-16 shrink-0 place-items-center rounded-full bg-accent text-base font-semibold text-white'>
            {initials || '?'}
          </div>
        )}
        <div className='w-full min-w-0 flex-1'>
          <FileDropZone
            accept={LOGO_ACCEPT}
            maxBytes={LOGO_MAX_BYTES}
            value={value}
            onUploaded={uploaded => {
              onChange(uploaded.documentId)
              persist.mutate(uploaded.documentId)
            }}
            onRemove={() => {
              onChange(null)
              persist.mutate(null)
            }}
          />
        </div>
      </div>
    </div>
  )
}
